import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { Dialog, DialogContent, DialogTitle } from '@/components/shadcn/dialog';
import { invoiceService } from '@/services/invoiceService';
import { InvoiceDetail } from '@/types/invoiceDetail';
import { formatRupiah } from '@/utils/cashflowUtils';

interface TransactionReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceNumber: string;
  arenaSlug: string;
  arenaInfo?: {
    name?: string;
    city?: string;
    address?: string;
  };
  fallbackInvoice?: any;
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
  'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'
];

const DAY_NAMES = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

function formatReceiptDateTime(dateStr?: string) {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const dayName = DAY_NAMES[d.getDay()];
    const date = d.getDate();
    const month = MONTH_NAMES[d.getMonth()];
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${dayName}, ${date} ${month}, ${hours}:${mins}`;
  } catch {
    return dateStr;
  }
}

export const TransactionReceiptModal: React.FC<TransactionReceiptModalProps> = ({
  isOpen,
  onClose,
  invoiceNumber,
  arenaSlug,
  arenaInfo,
  fallbackInvoice,
}) => {
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [waNumber, setWaNumber] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (isOpen && invoiceNumber && arenaSlug) {
      let isMounted = true;
      setLoading(true);
      invoiceService
        .getInvoiceDetail(arenaSlug, invoiceNumber)
        .then((res) => {
          if (!isMounted) return;
          const data = res?.data || null;
          setDetail(data);
          if (data?.customer_phone) {
            setWaNumber(data.customer_phone);
          } else if (fallbackInvoice?.customer_phone) {
            setWaNumber(fallbackInvoice.customer_phone);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch invoice receipt detail:', err);
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });

      return () => {
        isMounted = false;
      };
    } else {
      setDetail(null);
      setWaNumber('');
    }
  }, [isOpen, invoiceNumber, arenaSlug, fallbackInvoice]);

  if (!isOpen) return null;

  // Determine status
  const rawStatus = (detail?.status || fallbackInvoice?.payment_status || '').toLowerCase();
  const rawPaymentType = (detail?.payment_type || fallbackInvoice?.payment_type || '').toLowerCase();

  const isDP = rawStatus === 'partial' || rawPaymentType === 'dp' || rawPaymentType === 'dp_balance';
  const isPaid = rawStatus === 'paid';

  // Modal Title
  const modalTitle = isDP ? 'Struk DP' : isPaid ? 'Struk Pembayaran' : 'Struk Transaksi';

  // Arena Info
  const arenaName =
    detail?.arena_name ||
    arenaInfo?.name ||
    fallbackInvoice?.arena_name ||
    'Sporto Arena';
  const arenaCity =
    detail?.arena_address ||
    arenaInfo?.city ||
    arenaInfo?.address ||
    '';

  // Customer & Meta
  const customerName = detail?.customer_name || fallbackInvoice?.customer_name || 'Pelanggan';
  const displayInvoiceNumber = detail?.invoice_number || fallbackInvoice?.invoice_number || invoiceNumber;
  const isBookingOrder = (detail?.order_type || fallbackInvoice?.order_type) === 'field_booking';
  const metaLabel = isBookingOrder ? 'No. booking' : 'No. transaksi';

  // Field / Product Info
  const fieldName =
    detail?.field_bookings?.[0]?.field_name ||
    (isBookingOrder ? fallbackInvoice?.product : '');

  // Schedule Info for Booking
  let scheduleStr = '';
  if (detail?.field_bookings?.[0]) {
    const fb = detail.field_bookings[0];
    const dateStr = fb.booking_date;
    let formattedDate = dateStr;
    if (dateStr) {
      try {
        const d = new Date(dateStr);
        if (!isNaN(d.getTime())) {
          formattedDate = `${DAY_NAMES[d.getDay()]}, ${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`;
        }
      } catch {}
    }
    const startTime = fb.start_time ? fb.start_time.slice(0, 5) : '';
    const endTime = fb.end_time ? fb.end_time.slice(0, 5) : '';
    scheduleStr = [formattedDate, startTime && endTime ? `${startTime}-${endTime}` : ''].filter(Boolean).join(', ');
  } else {
    scheduleStr = formatReceiptDateTime(detail?.created_at || fallbackInvoice?.transaction_date);
  }

  // Payment method
  const paymentMethod =
    detail?.payment_method ||
    fallbackInvoice?.payment_method ||
    fallbackInvoice?.payment_type ||
    'Cash';

  // Amounts
  const totalAmount =
    detail?.total_booking_amount ||
    detail?.total_amount ||
    fallbackInvoice?.amount ||
    fallbackInvoice?.subtotal ||
    fallbackInvoice?.total_amount ||
    0;

  let paidAmount = 0;
  let remainingAmount = totalAmount;

  if (isPaid) {
    paidAmount = totalAmount;
    remainingAmount = 0;
  } else if (isDP) {
    paidAmount =
      detail?.paid_amount && detail.paid_amount > 0
        ? detail.paid_amount
        : totalAmount / 2;
    remainingAmount = Math.max(0, totalAmount - paidAmount);
  } else {
    // Unpaid / Pending
    paidAmount =
      detail?.paid_amount && detail.paid_amount < totalAmount
        ? detail.paid_amount
        : 0;
    remainingAmount = Math.max(0, totalAmount - paidAmount);
  }

  // Items list
  const hasItems =
    (detail?.product_items && detail.product_items.length > 0) ||
    (detail?.field_bookings && detail.field_bookings.length > 0);

  const handlePrint = async () => {
    if (isPrinting) return;
    try {
      setIsPrinting(true);
      const blob = await invoiceService.downloadInvoicePdf(arenaSlug, displayInvoiceNumber);
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
          } catch (e) {
            console.error('Iframe print error, opening in new window:', e);
            window.open(blobUrl, '_blank');
          }
        }, 300);
      };
    } catch (err) {
      console.error('Failed to print PDF:', err);
      window.print();
    } finally {
      setIsPrinting(false);
    }
  };

  const handleSendWa = () => {
    let cleanPhone = waNumber.replace(/\D/g, '');
    if (cleanPhone.startsWith('0')) {
      cleanPhone = '62' + cleanPhone.slice(1);
    }
    if (!cleanPhone) {
      alert('Silakan masukkan nomor WhatsApp terlebih dahulu.');
      return;
    }

    const lines = [
      `*${modalTitle.toUpperCase()}*`,
      `*${arenaName}*`,
      arenaCity ? arenaCity : '',
      '--------------------------------',
      `${metaLabel}: ${displayInvoiceNumber}`,
      `Pelanggan: ${customerName}`,
      fieldName ? `Lapangan: ${fieldName}` : '',
      `Waktu: ${scheduleStr}`,
      `Metode: ${paymentMethod}`,
      '--------------------------------',
      isDP ? `Total sewa: ${formatRupiah(totalAmount)}` : `Total: ${formatRupiah(totalAmount)}`,
      isDP ? `DP dibayar: ${formatRupiah(paidAmount)}` : `Dibayar: ${formatRupiah(paidAmount)}`,
      `Sisa tagihan: ${formatRupiah(remainingAmount)}`,
      `Status: ${isPaid ? 'Lunas' : isDP ? 'DP' : 'Belum Lunas'}`,
      '--------------------------------',
      'Terima kasih sudah bermain. Simpan struk ini sebagai bukti pembayaran.',
    ].filter(Boolean);

    const text = encodeURIComponent(lines.join('\n'));
    window.open(`https://api.whatsapp.com/send?phone=${cleanPhone}&text=${text}`, '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[440px] p-0 overflow-hidden rounded-3xl border border-[#E0E6ED] bg-[#F8F9FA]">
        {/* Header Dialog */}
        <div className="flex items-center justify-between border-b border-[#E0E6ED] bg-white px-5 py-4">
          <DialogTitle className="text-sm sm:text-base font-semibold text-[#0E1726]">
            {modalTitle}
          </DialogTitle>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-5 max-h-[85vh] overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon icon="ph:spinner" className="h-8 w-8 animate-spin text-[#FC480D]" />
              <p className="mt-2 text-xs text-gray-500">Memuat struk transaksi...</p>
            </div>
          ) : (
            <>
              {/* Receipt Card */}
              <div
                id="transaction-receipt-card"
                className="rounded-2xl border border-[#E0E6ED] bg-white p-5 shadow-xs select-none"
              >
                {/* Logo & Arena */}
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center gap-1.5 mb-1">
                    <img
                      src="https://cdn.sporto.id/sporto-black.png"
                      alt="sporto"
                      className="h-6 w-auto object-contain"
                    />
                  </div>
                  <h4 className="text-xs sm:text-[13px] font-semibold text-[#0E1726]">
                    {arenaName}
                  </h4>
                  {arenaCity && (
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {arenaCity}
                    </p>
                  )}
                </div>

                {/* Dashed line */}
                <div className="my-3.5 border-b border-dashed border-[#E0E6ED]" />

                {/* Metadata */}
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-gray-500">{metaLabel}</span>
                    <span className="font-mono font-semibold text-[#0E1726]">
                      {displayInvoiceNumber}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-gray-500">Pelanggan</span>
                    <span className="font-medium text-[#0E1726] text-right">
                      {customerName}
                    </span>
                  </div>
                  {fieldName && (
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-gray-500">Lapangan</span>
                      <span className="font-medium text-[#0E1726] text-right">
                        {fieldName}
                      </span>
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-gray-500">{isBookingOrder ? 'Jadwal' : 'Waktu'}</span>
                    <span className="font-medium text-[#0E1726] text-right">
                      {scheduleStr}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-gray-500">Metode</span>
                    <span className="font-medium text-[#0E1726] text-right">
                      {paymentMethod}
                    </span>
                  </div>
                </div>

                {/* Dashed line */}
                <div className="my-3.5 border-b border-dashed border-[#E0E6ED]" />

                {/* Items */}
                <div className="space-y-2 text-xs">
                  {hasItems ? (
                    <>
                      {/* Products */}
                      {detail?.product_items?.map((item) => (
                        <div key={item.id} className="flex items-start justify-between">
                          <div>
                            <div className="font-medium text-[#0E1726]">{item.product_name}</div>
                            <div className="text-[11px] text-gray-400">
                              {item.quantity} × {formatRupiah(item.unit_price)}
                            </div>
                          </div>
                          <div className="font-semibold text-[#0E1726]">
                            {formatRupiah(item.total_price)}
                          </div>
                        </div>
                      ))}

                      {/* Field Bookings */}
                      {detail?.field_bookings?.map((fb) => (
                        <div key={fb.id} className="flex items-start justify-between">
                          <div>
                            <div className="font-medium text-[#0E1726]">{fb.field_name || 'Booking Lapangan'}</div>
                            <div className="text-[11px] text-gray-400">
                              {fb.total_slots || 1} slot · {fb.sport_category || 'Sewa Lapangan'}
                            </div>
                          </div>
                          <div className="font-semibold text-[#0E1726]">
                            {formatRupiah(fb.final_price || totalAmount)}
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium text-[#0E1726]">
                          {fallbackInvoice?.product || 'Sewa / Produk'}
                        </div>
                        <div className="text-[11px] text-gray-400">1 × {formatRupiah(totalAmount)}</div>
                      </div>
                      <div className="font-semibold text-[#0E1726]">
                        {formatRupiah(totalAmount)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Dashed line */}
                <div className="my-3.5 border-b border-dashed border-[#E0E6ED]" />

                {/* Summary / Totals */}
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">{isDP ? 'Total sewa' : 'Total'}</span>
                    <span className="font-semibold text-[#0E1726]">{formatRupiah(totalAmount)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">{isDP ? 'DP dibayar' : 'Dibayar'}</span>
                    <span className="font-semibold text-[#0E1726]">{formatRupiah(paidAmount)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Sisa tagihan</span>
                    <span className="font-semibold text-[#FC480D]">{formatRupiah(remainingAmount)}</span>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="mt-4 flex justify-center">
                  {isPaid ? (
                    <span className="inline-block rounded-full border border-[#027A48] bg-[#ECFDF3] px-5 py-1 text-xs font-semibold text-[#027A48]">
                      Lunas
                    </span>
                  ) : isDP ? (
                    <span className="inline-block rounded-full border border-[#FC480D] bg-[#FFF5EE] px-5 py-1 text-xs font-semibold text-[#FC480D]">
                      DP
                    </span>
                  ) : (
                    <span className="inline-block rounded-full border border-red-500 bg-red-50 px-5 py-1 text-xs font-semibold text-red-600">
                      Belum Lunas
                    </span>
                  )}
                </div>

                {/* Footer Note */}
                <p className="mt-3.5 text-center text-[10.5px] leading-relaxed text-gray-400">
                  Terima kasih sudah bermain. Simpan struk ini sebagai bukti pembayaran.
                </p>
              </div>

              {/* Action Form */}
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-[#0E1726] mb-1.5">
                    No. WhatsApp
                  </label>
                  <input
                    type="text"
                    value={waNumber}
                    onChange={(e) => setWaNumber(e.target.value)}
                    placeholder="08xx-xxxx-xxxx"
                    className="w-full rounded-xl border border-[#E0E6ED] bg-white px-3.5 py-2.5 text-xs text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none"
                  />
                </div>

                <div className="flex items-center gap-2.5 pt-1">
                  <button
                    type="button"
                    disabled={isPrinting}
                    onClick={handlePrint}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-[#E0E6ED] bg-white py-2.5 text-xs font-semibold text-gray-700 shadow-2xs transition-colors hover:bg-gray-50 hover:text-[#0E1726] disabled:opacity-60"
                  >
                    {isPrinting ? (
                      <Icon icon="ph:spinner" className="h-4 w-4 animate-spin text-[#FC480D]" />
                    ) : (
                      <Icon icon="ph:printer-bold" className="h-4 w-4" />
                    )}
                    <span>{isPrinting ? 'Menyiapkan...' : 'Cetak'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleSendWa}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#FC480D] py-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#E8490F]"
                  >
                    <Icon icon="ph:paper-plane-tilt-bold" className="h-4 w-4" />
                    <span>Kirim ke WA</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionReceiptModal;
